'use client'

import * as React from 'react'
import { id } from 'date-fns/locale'
import { CalendarIcon, ChevronDownIcon } from 'lucide-react'

import cn from '@config/tailwind/cn'

import { Button } from '../components/button'
import { Calendar } from '../components/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '../components/popover'
import { typographyVariants } from './typography'

export interface DatePreset {
  label: string
  icon?: React.ReactNode
  resolve: () => Date | null
  /** When true, the resolved date is used as-is without wrapping with endOfDay. */
  exact?: boolean
}

export interface DatePresetPickerProps {
  presets: DatePreset[]
  value?: Date | null
  onChange?: (value: Date | null) => void
  /**
   * Render prop for the trigger button.
   * `open` reflects current popover open state — use it to rotate chevrons etc.
   * The returned element MUST accept and spread standard HTML button/div props
   * (onClick, aria-expanded, disabled) so Base UI can inject its accessibility wiring.
   *
   * If omitted, a built-in trigger is rendered using `placeholder` and `size`.
   */
  trigger?: (open: boolean, label: string | null) => React.ReactElement
  /** Placeholder text shown in the built-in trigger when no date is selected. Ignored when `trigger` is provided. */
  placeholder?: string
  /** Height variant for the built-in trigger. Ignored when `trigger` is provided. */
  size?: 'default' | 'sm'
  disabled?: boolean
  /** Disables any selectable date (preset or calendar day) on or before this date. */
  minDate?: Date
}

type View = 'presets' | 'calendar'

function formatIndonesianDate(date: Date): string {
  return new Intl.DateTimeFormat('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date)
}

function endOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

function isBeforeToday(date: Date): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date < today
}

function defaultTriggerClass(size: 'default' | 'sm'): string {
  return cn(
    'ui:flex ui:w-full ui:min-w-0 ui:items-center ui:justify-between ui:gap-1.5 ui:font-normal',
    'ui:border ui:border-outline ui:bg-neutral-white ui:text-neutral-black',
    'ui:px-2.5 ui:py-2 ui:whitespace-nowrap ui:transition-colors ui:outline-none ui:select-none',
    'ui:focus-visible:border-blue-100 ui:focus-visible:ring-3 ui:focus-visible:ring-blue-20',
    'ui:disabled:cursor-not-allowed ui:disabled:bg-grey-10 ui:disabled:opacity-50',
    size === 'default' ? 'ui:h-12 ui:rounded-lg' : 'ui:h-8 ui:rounded-[min(var(--radius-md),10px)]',
  )
}

function makeDefaultTrigger(
  open: boolean,
  label: string | null,
  placeholder: string,
  size: 'default' | 'sm',
): React.ReactElement {
  return (
    <Button baseButton variant='none' className={defaultTriggerClass(size)}>
      <span className='ui:flex ui:flex-1 ui:items-center ui:gap-1.5 ui:truncate ui:text-left ui:text-base'>
        <CalendarIcon className='ui:size-5 ui:shrink-0' />
        <span>{label ?? placeholder}</span>
      </span>
      <ChevronDownIcon
        className={cn('ui:size-4 ui:shrink-0 ui:text-blue-100 ui:transition-transform', open && 'ui:rotate-180')}
      />
    </Button>
  )
}

export function DatePresetPicker({
  presets,
  value,
  onChange,
  trigger,
  placeholder = 'Pilih tanggal',
  size = 'default',
  disabled,
  minDate,
}: DatePresetPickerProps) {
  const [open, setOpen] = React.useState(false)
  const [view, setView] = React.useState<View>('presets')
  const [pendingDate, setPendingDate] = React.useState<Date | undefined>(undefined)
  const [selectedLabel, setSelectedLabel] = React.useState<string | null>(null)

  const displayLabel = selectedLabel ?? (value ? formatIndonesianDate(value) : null)

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (!nextOpen) setView('presets')
  }

  function handlePresetSelect(preset: DatePreset) {
    const resolved = preset.resolve()
    onChange?.(resolved !== null ? (preset.exact ? resolved : endOfDay(resolved)) : null)
    setSelectedLabel(preset.label)
    setOpen(false)
    setView('presets')
  }

  function handleCalendarConfirm() {
    onChange?.(pendingDate ? endOfDay(pendingDate) : null)
    setSelectedLabel(pendingDate ? formatIndonesianDate(pendingDate) : null)
    setPendingDate(undefined)
    setOpen(false)
    setView('presets')
  }

  function handleCalendarClear() {
    onChange?.(null)
    setSelectedLabel(null)
    setPendingDate(undefined)
    setOpen(false)
    setView('presets')
  }

  function handleCalendarCancel() {
    setPendingDate(undefined)
    setView('presets')
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={trigger ? trigger(open, displayLabel) : makeDefaultTrigger(open, displayLabel, placeholder, size)}
        disabled={disabled}
      />
      <PopoverContent
        align='start'
        className={cn(
          'ui:w-auto ui:gap-0 ui:p-0 ui:overflow-hidden',
          view === 'presets' ? 'ui:min-w-(--anchor-width)' : 'ui:max-w-[360px]',
        )}
      >
        {view === 'presets' && (
          <PresetView
            presets={presets}
            onSelect={handlePresetSelect}
            minDate={minDate}
            onCalendarTrigger={() => {
              setPendingDate(value ?? undefined)
              setView('calendar')
            }}
          />
        )}
        {view === 'calendar' && (
          <CalendarView
            pendingDate={pendingDate}
            onPendingDateChange={setPendingDate}
            minDate={minDate}
            onConfirm={handleCalendarConfirm}
            onClear={handleCalendarClear}
            onCancel={handleCalendarCancel}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}

interface PresetViewProps {
  presets: DatePreset[]
  onSelect: (preset: DatePreset) => void
  onCalendarTrigger: () => void
  minDate?: Date
}

function PresetView({ presets, onSelect, onCalendarTrigger, minDate }: PresetViewProps) {
  const itemClass = cn(
    'ui:flex ui:w-full ui:cursor-pointer ui:items-center ui:gap-2 ui:px-3 ui:py-2.5 ui:text-left',
    'ui:hover:bg-blue-10 ui:transition-colors ui:outline-none ui:focus-visible:bg-blue-10',
    'ui:disabled:opacity-40 ui:disabled:cursor-not-allowed ui:disabled:pointer-events-none',
    typographyVariants({ variant: 'body-lg' }),
  )

  return (
    <div className='ui:py-1'>
      {presets.map((preset) => {
        const resolved = preset.resolve()
        const isDisabled = minDate !== undefined && resolved !== null && endOfDay(resolved) <= minDate
        return (
          <button
            type='button'
            key={preset.label}
            onClick={() => onSelect(preset)}
            disabled={isDisabled}
            className={itemClass}
          >
            {preset.icon}
            {preset.label}
          </button>
        )
      })}
      <div className='ui:bg-outline ui:mx-2 ui:my-1 ui:h-px' />
      <button type='button' onClick={onCalendarTrigger} className={itemClass}>
        <CalendarIcon className='ui:size-4' />
        Pilih tanggal
      </button>
    </div>
  )
}

interface CalendarViewProps {
  pendingDate: Date | undefined
  onPendingDateChange: (date: Date | undefined) => void
  onConfirm: () => void
  onClear: () => void
  onCancel: () => void
  minDate?: Date
}

function CalendarView({ pendingDate, onPendingDateChange, onConfirm, onClear, onCancel, minDate }: CalendarViewProps) {
  return (
    <div className='ui:w-full'>
      <div className='ui:px-3 ui:py-4 ui:border-b ui:border-outline-variant ui:flex ui:flex-col ui:gap-y-6'>
        <p className={typographyVariants({ variant: 'label-sm' })}>Pilih tanggal</p>
        {pendingDate ? (
          <p className={typographyVariants({ variant: 'headline-sm' })}>{formatIndonesianDate(pendingDate)}</p>
        ) : (
          <div className='ui:h-2' />
        )}
      </div>
      <Calendar
        mode='single'
        className='ui:w-full'
        locale={id}
        formatters={{
          formatWeekdayName: (date) => date.toLocaleDateString('id-ID', { weekday: 'short' }).charAt(0).toUpperCase(),
        }}
        selected={pendingDate}
        onSelect={onPendingDateChange}
        disabled={(date) => isBeforeToday(date) || (minDate !== undefined && endOfDay(date) <= minDate)}
      />
      <div className='ui:flex ui:items-center ui:justify-between ui:px-2 ui:py-2'>
        <Button variant='ghost' size='sm' onClick={onClear}>
          Kosongkan
        </Button>
        <div className='ui:flex ui:gap-1'>
          <Button variant='ghost' size='sm' onClick={onCancel}>
            Batal
          </Button>
          <Button variant='ghost' size='sm' onClick={onConfirm}>
            OK
          </Button>
        </div>
      </div>
    </div>
  )
}
